import { creatorbase } from '@round/api';
import { Modal } from '@round/whitelabel-ui';
import { Button } from '@round/whitelabel-ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { creatorbaseQueries } from '@round/api';
import { showNotification } from 'helpers';
import styles from './DeletePurchaseOrderModal.module.css';
import cn from 'classnames';
import { Banner } from '@round/whitelabel-ui';
import { useBillingEntity, useInvalidatePurchaseOrders } from '@round/creatorbase-helpers/api-hooks';
import { isNumber } from '../../../../../../utility/utility';
import { SkeletonizedValue } from '@round/ui-kit';

const option = creatorbaseQueries.purchaseOrders.options.delete;
const utils = creatorbaseQueries.purchaseOrders.utils.delete;

type Props = {
    isOpen: boolean;
    onClose: () => void;
    purchaseOrder: creatorbase.PurchaseOrder | null;
};

const DeletePurchaseOrderModal = ({ isOpen, onClose, purchaseOrder }: Props) => {
    const queryClient = useQueryClient();
    const { mutate: deletePurchaseOrder, isPending } = useMutation(option(purchaseOrder?.id, queryClient));
    const { data: billingEntity, status: billingEntityStatus } = useBillingEntity(purchaseOrder?.billing_entity_id, {
        enabled: isNumber(purchaseOrder?.billing_entity_id) && isOpen,
    });
    const { invalidatePurchaseOrders } = useInvalidatePurchaseOrders();

    const number = purchaseOrder?.number || purchaseOrder?.request_reference;
    return (
        <Modal className={styles.modal} closeOnEscape closeOnOverlayClick isOpen={isOpen} onClose={onClose}>
            <Modal.Header>
                <Modal.Title>Delete Purchase Order</Modal.Title>
            </Modal.Header>

            <Modal.Body className={styles.body}>
                <p className={cn(styles.copy, styles.bold)}>You're about to permanently delete the following:</p>
                <Banner appearance="destructive">
                    <p className={styles.copy}>
                        <span className={styles.bold}>Purchase Order: </span>
                        {number}
                    </p>
                    <p className={styles.copy}>
                        <span className={styles.bold}>Description: </span>
                        {purchaseOrder?.description}
                    </p>

                    <p className={styles.copy}>
                        <span className={styles.bold}>Billing Entity: </span>
                        <SkeletonizedValue isInitialized={billingEntityStatus !== 'pending'} width="5rem">
                            {billingEntity?.name}
                        </SkeletonizedValue>
                    </p>
                </Banner>
                <p className={styles.copy}>You cannot restore this purchase order. It will be deleted permanently.</p>
            </Modal.Body>

            <Modal.Actions>
                <Button
                    appearance="critical"
                    isLoading={isPending}
                    disabled={!purchaseOrder}
                    onClick={() => {
                        deletePurchaseOrder(undefined, {
                            onSuccess: () => {
                                onClose();
                                showNotification('Purchase Order deleted', 'info');
                                invalidatePurchaseOrders(purchaseOrder?.id);
                            },
                            onError: (error) => {
                                if (!utils.isExpectedErrorResponse(error)) {
                                    showNotification('Could not delete Purchase Order', 'error');
                                    return;
                                }

                                showNotification(error.response.data.detail, 'error');
                            },
                        });
                    }}
                >
                    Delete Purchase Order
                </Button>

                <Button appearance="ghost" onClick={onClose}>
                    Cancel
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default DeletePurchaseOrderModal;
